import { defineMessages, type MessageDescriptor } from 'react-intl'

export const CHAT_TOOL_LOADING_KEYS = [
  'search_hotels',
  'get_similar_offers',
  'get_flash_lead_prices',
  'search_experiences',
  'search_cruises',
  'search_airport_transfers',
  'search_airport_lounge',
  'search_offer_reviews',
  'search_flight_sales',
  'search_flights',
  'search_flight_calendar_grid',
  'get_offer_reviews',
  'get_weather_forecast',
  'get_currency_exchange_rates',
  'lookup_google_places',
  'get_itinerary',
  'update_itinerary',
  'save_itinerary_structure',
  'save_itinerary_items',
  'save_itinerary_transport',
  'remove_itinerary_entries',

  'lookup_offer',
  'lookup_offer_general_availability',
  'lookup_offer_specific_availability',
  'get_orders',
  'get_order_details',
  'get_experience_voucher_details',
  'get_flight_schedule',
  'create_trip_itinerary_export',
  'get_wishlist_items',
  'get_customer_shopping_carts',
  'get_customer_wallet',
  'get_communications_preferences',
  'update_communications_preferences',
  'get_recent_customer_emails',
  'research_support_articles',
  'get_chat_name',
  'rename_chat',
  'submit_wedding_inquiry',
] as const

export type ChatToolLoadingKey = typeof CHAT_TOOL_LOADING_KEYS[number]

const chatToolLoadingKeysSet: Set<string> = new Set(CHAT_TOOL_LOADING_KEYS)

const CHAT_TOOL_LOADING_MESSAGE_DESCRIPTORS: Record<
  ChatToolLoadingKey,
  MessageDescriptor
> = defineMessages({
  search_hotels: {
    id: 'yBe6X4ES0Li8tGm3nm1H',
    defaultMessage: 'Searching for the best hotels',
    description: 'Loading message while AI is searching hotels in trip planner chat',
  },
  get_similar_offers: {
    id: 'kwkXBY5s5NYdcApSW2ju',
    defaultMessage: 'Finding similar offers',
    description: 'Loading message while AI is finding similar hotel offers in trip planner chat',
  },
  get_flash_lead_prices: {
    id: 'KfiKT7RXnsWZHqDhdXWC',
    defaultMessage: 'Checking the best hotel offers',
    description: 'Loading message while AI is checking flash hotel lead prices in trip planner chat',
  },
  search_experiences: {
    id: '67T9n-vMNPU2IlUf5aJn',
    defaultMessage: 'Searching for the best experiences',
    description: 'Loading message while AI is searching experiences in trip planner chat',
  },
  search_cruises: {
    id: 'Bzvtw-69EMwRoD_gJ4Py',
    defaultMessage: 'Searching for the best cruises',
    description: 'Loading message while AI is searching cruises in trip planner chat',
  },
  search_airport_transfers: {
    id: 'x91C2XzutbJ-eZJuOm-y',
    defaultMessage: 'Searching for airport transfers',
    description: 'Loading message while AI is searching airport transfers in trip planner chat',
  },
  search_airport_lounge: {
    id: 'syldCQ2jEg2tgtxOf0S1',
    defaultMessage: 'Searching for airport lounges',
    description: 'Loading message while AI is searching airport lounges in trip planner chat',
  },
  search_offer_reviews: {
    id: 'fvsxhh7BquFRH8LcE5On',
    defaultMessage: 'Searching reviews',
    description: 'Loading message while AI is searching reviews for a hotel or experience in trip planner chat',
  },
  search_flight_sales: {
    id: '6yO-_vrdKFVrs8v5XxQx',
    defaultMessage: 'Checking flight sales',
    description: 'Loading message while AI is checking flight sale campaigns and route deals in trip planner chat',
  },
  search_flights: {
    id: 't6FkBnY7TlTSlNIAKAET',
    defaultMessage: 'Searching for the best flights',
    description: 'Loading message while AI is searching flights in trip planner chat',
  },
  search_flight_calendar_grid: {
    id: 'nZaTqJ1iiJlGGawocb_s',
    defaultMessage: 'Checking flexible flight dates',
    description: 'Loading message while AI is checking flexible flight date prices in trip planner chat',
  },
  get_offer_reviews: {
    id: 'mdO_ubzsZQEsIf63LMEy',
    defaultMessage: 'Reading recent reviews',
    description: 'Loading message while AI is looking up hotel or experience reviews in trip planner chat',
  },
  get_weather_forecast: {
    id: '7WdoppFbRUpPNRz4jhai',
    defaultMessage: 'Checking the weather forecast',
    description: 'Loading message while AI is checking the current weather and daily forecast in trip planner chat',
  },
  get_currency_exchange_rates: {
    id: '-LVNtaePdDuN7BVe8BYY',
    defaultMessage: 'Checking currency rates',
    description: 'Loading message while AI is checking current currency exchange rates in trip planner chat',
  },
  lookup_google_places: {
    id: 'JBie0m5OAB9WMrCWN_h8',
    defaultMessage: 'Looking up places',
    description: 'Loading message while AI is searching Google Places for real-world place information in trip planner chat',
  },
  get_itinerary: {
    id: 'GuoPDJaHXrW-eYSUl6K1',
    defaultMessage: 'Checking your itinerary',
    description: 'Loading message while AI is checking the current trip itinerary in trip planner chat',
  },
  update_itinerary: {
    id: 'RZTXJ4O8Yjvi-bga_LWf',
    defaultMessage: 'Updating your itinerary',
    description: 'Loading message while AI is updating the current trip itinerary in trip planner chat',
  },
  save_itinerary_structure: {
    id: 'RZTXJ4O8Yjvi-bga_LWf',
    defaultMessage: 'Updating your itinerary',
    description: 'Loading message while AI is updating the current trip itinerary in trip planner chat',
  },
  save_itinerary_items: {
    id: 'RZTXJ4O8Yjvi-bga_LWf',
    defaultMessage: 'Updating your itinerary',
    description: 'Loading message while AI is updating the current trip itinerary in trip planner chat',
  },
  save_itinerary_transport: {
    id: 'RZTXJ4O8Yjvi-bga_LWf',
    defaultMessage: 'Updating your itinerary',
    description: 'Loading message while AI is updating the current trip itinerary in trip planner chat',
  },
  remove_itinerary_entries: {
    id: 'RZTXJ4O8Yjvi-bga_LWf',
    defaultMessage: 'Updating your itinerary',
    description: 'Loading message while AI is updating the current trip itinerary in trip planner chat',
  },
  lookup_offer: {
    id: 'ng_ya2kVg_dMf0mghVsr',
    defaultMessage: 'Looking up offer details',
    description: 'Loading message while AI is looking up a hotel or experience offer in trip planner chat',
  },
  lookup_offer_general_availability: {
    id: '4mRwhkZGfkC1ut-b-jgb',
    defaultMessage: 'Checking broad availability',
    description: 'Loading message while AI is checking broad hotel or experience availability in trip planner chat',
  },
  lookup_offer_specific_availability: {
    id: 'gOom2-9vKSPRplCBeYRu',
    defaultMessage: 'Checking exact availability',
    description: 'Loading message while AI is checking exact hotel or experience availability in trip planner chat',
  },
  get_orders: {
    id: '8Zw3m_CYNhE-so6hyR51',
    defaultMessage: 'Looking through your order history',
    description: 'Loading message while AI is looking through the user order history in trip planner chat',
  },
  get_order_details: {
    id: 'OCCKxhBX87pJCMVkFEu-',
    defaultMessage: 'Looking up your booking details',
    description: 'Loading message while AI is looking up details for a specific user order in trip planner chat',
  },
  get_experience_voucher_details: {
    id: 'G2E3t5STt4ZhpcNXKGUg',
    defaultMessage: 'Checking your experience voucher',
    description: 'Loading message while AI checks experience voucher details in trip planner chat',
  },
  get_flight_schedule: {
    id: '1ZPjiU1IEzOrheMMVYKG',
    defaultMessage: 'Checking flight schedules',
    description: 'Loading message while AI checks flight schedules in trip planner chat',
  },
  create_trip_itinerary_export: {
    id: '-orvuNmk5E4FAOACBCSO',
    defaultMessage: 'Preparing your itinerary download',
    description: 'Loading message while AI prepares an itinerary export in trip planner chat',
  },
  get_wishlist_items: {
    id: 'czgNLb6UO2eIL5nV7QvL',
    defaultMessage: 'Checking your wishlists',
    description: 'Loading message while AI is checking the user wishlists in trip planner chat',
  },
  get_customer_shopping_carts: {
    id: 'pgFQNM-5CzHbmCVBI30F',
    defaultMessage: 'Checking your shopping carts',
    description: 'Loading message while AI is checking the user shopping carts in trip planner chat',
  },
  get_customer_wallet: {
    id: 'hO8n4J8ktBwdgW5X5jxp',
    defaultMessage: 'Checking your profile',
    description: 'Loading message while AI is checking the user profile, loyalty, membership, credits, and vouchers in trip planner chat',
  },
  get_communications_preferences: {
    id: 'NATjb_aq7LscmKzxBRgj',
    defaultMessage: 'Checking your communications preferences',
    description: 'Loading message while AI is checking the user communication preferences in trip planner chat',
  },
  update_communications_preferences: {
    id: 'rdFu-JnKAV2AL13tBJ09',
    defaultMessage: 'Reviewing your communication preference changes',
    description: 'Loading message while AI is reviewing requested communication preference changes in trip planner chat before the customer confirms them',
  },
  get_recent_customer_emails: {
    id: 'TVk4W0LlHvbcZ6oLDf8Q',
    defaultMessage: 'Reading your recent emails',
    description: 'Loading message while AI is reading the user recent email archive in trip planner chat',
  },
  research_support_articles: {
    id: 'SniPsSGluiZ_yZBuQsyn',
    defaultMessage: 'Searching support articles',
    description: 'Loading message while AI is searching customer support knowledge articles in trip planner chat',
  },
  get_chat_name: {
    id: 'xaO-qA6JU4kEYvPrLS8G',
    defaultMessage: 'Checking your chat name',
    description: 'Loading message while AI is checking the current trip or chat name in trip planner chat',
  },
  rename_chat: {
    id: '_XLRekmFkBCpVTpdAyj4',
    defaultMessage: 'Renaming your chat',
    description: 'Loading message while AI is renaming the current trip or chat in trip planner chat',
  },
  submit_wedding_inquiry: {
    id: 'Ks4ngwzMOKXFQbRdPsfz',
    defaultMessage: 'Preparing your wedding enquiry',
    description: 'Loading message while AI is preparing the user destination wedding concierge enquiry in trip planner chat',
  },
})

export function isChatToolLoadingKey(value: unknown): value is ChatToolLoadingKey {
  return typeof value === 'string' && chatToolLoadingKeysSet.has(value)
}

export function getChatToolLoadingMessageDescriptor(
  key: ChatToolLoadingKey,
): MessageDescriptor {
  return CHAT_TOOL_LOADING_MESSAGE_DESCRIPTORS[key]
}
